import { Star } from "lucide-react";

export const GoogleG = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 48 48" className={`${className} shrink-0`} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.2l7.9 6.2C12.5 13.6 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17z" />
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C1 16.6 0 20.2 0 24s1 7.4 2.7 10.8l7.9-6.2z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.2C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

const linkProps = (url) => (url ? { href: url, target: "_blank", rel: "noreferrer", "aria-label": "Read our Google reviews" } : {});
const countLabel = (rating) => rating.count_label || Number(rating.count).toLocaleString("en-SG");

export const RatingBadge = ({ rating, testid, large }) => {
  const Tag = rating.url ? "a" : "div";
  return (
    <Tag data-testid={testid} {...linkProps(rating.url)} className={`inline-flex items-center gap-3 rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 ${rating.url ? "transition-transform duration-300 hover:-translate-y-0.5" : ""} ${large ? "px-5 py-3.5" : "px-4 py-2.5"}`}>
      <GoogleG />
      <span className={`lp-display font-extrabold text-slate-900 ${large ? "text-4xl" : "text-2xl"}`}>{Number(rating.rating).toFixed(1)}</span>
      <div className="leading-tight">
        <span className="flex text-[#fbbc04]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className={`${large ? "h-6 w-6" : "h-5 w-5"} fill-current`} />)}</span>
        <span className={`text-slate-600 ${large ? "text-sm" : "text-xs"}`}>from <b className="text-slate-900">{countLabel(rating)}</b> Google reviews</span>
      </div>
    </Tag>
  );
};

const Cell = ({ value, label, children, testid, as: Tag = "div", ...rest }) => (
  <Tag data-testid={testid} {...rest} className="flex items-start gap-3 px-5 py-3.5 sm:flex-col sm:gap-1 sm:py-4">
    <div className="flex shrink-0 items-center gap-2">{children}<span className="lp-display text-3xl font-extrabold leading-none text-slate-900 sm:text-[34px]">{value}</span></div>
    <span className="text-[13px] leading-snug text-slate-600" data-testid={testid && `${testid}-label`}>{label}</span>
  </Tag>
);

export const HeroStats = ({ rating, stats, color }) => (
  <div className="mb-7 max-w-xl" data-testid="lp-hero-stats">
    <div className="grid divide-y divide-slate-200 overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {rating && (
        <Cell as={rating.url ? "a" : "div"} {...linkProps(rating.url)} testid="lp-rating-hero" value={Number(rating.rating).toFixed(1)}
          label={<><span className="mr-1 inline-flex align-[-2px] text-[#fbbc04]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</span><b className="text-slate-900">{countLabel(rating)}</b> Google reviews</>}>
          <GoogleG className="h-6 w-6" />
        </Cell>
      )}
      {stats?.result && <Cell testid="lp-stat-result" value={<span style={{ color }}>{stats.result.value}</span>} label={<>{stats.result.label}<sup>*</sup></>} />}
      {stats?.patients && <Cell testid="lp-stat-patients" value={stats.patients.value} label={stats.patients.label} />}
    </div>
    {stats?.result && <p className="mt-2 text-[11px] text-white/60" data-testid="lp-stat-source">*{stats.result.source}</p>}
  </div>
);

export const ResultStat = ({ stat, light, testid }) => stat ? (
  <div className="max-w-sm" data-testid={testid}>
    <div className="flex flex-col items-start gap-2 sm:flex-row sm:gap-3">
      <span className="lp-display text-5xl font-extrabold leading-none" style={{ color: light ? "#a9d3f5" : undefined }} data-testid={`${testid}-value`}>{stat.value}</span>
      <span className={`pt-px text-base font-semibold leading-snug sm:-mt-0.5 ${light ? "text-white" : "text-slate-900"}`} data-testid={`${testid}-text`}>of our patients {stat.label}<sup>*</sup></span>
    </div>
    <p className={`mt-1.5 text-[11px] ${light ? "text-white/60" : "text-slate-500"}`}>*{stat.source}</p>
  </div>
) : null;
